import React, { useState, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'
import Input from '../../../../../components/input'
import Button from '../../../../../components/button'
import PersonAdd from '@material-ui/icons/PersonAdd'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import { useChatContext } from 'stream-chat-react'
import config from '../../../../../config'
import { sendGroupNotification, getOrCreatePortalChannel, logGroupMessage } from '../../../../../api'
import { AuthContext } from '../../../../../auth-context'
import { DashboardContext } from '../../../dashboard-context'
import Modal from '../../../../../components/modal'
import { getFirstName } from '../../../views/inspect/util'
import { ErrorMessage } from '../../../../../components/error-message'
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';

const useStyles = makeStyles(theme => ({
  buttonProgress: {
    color: '#2dd042',
  },
}));

const Container = styled.div`
  background: #FFF;
	border-radius: 8px;
	box-shadow: 0px 0px 8px 0px #e1e2e6;
  width: 540px;
  padding: 24px;

  @media(max-width: 900px) {
    padding: 16px;
  }
`

const Label = styled.label`
  color: #555;
  display: flex;
  align-items: center;
  font-family: ${props => props.theme.font.primary};
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 24px;

  svg {
    fill: #555;
    height: auto;
    width: 36px;
    margin-right: 16px;
  }

  @media(max-width: 900px) {
    font-size: 1.25rem;
    margin-bottom: 16px;

    svg {
      width: 28px;
    }
  }

  @media(max-width: 500px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const ClearButton = styled(Button)`
  background: none;
  box-shadow: none;
  color: #888;
  margin: 0;

  &:hover {
    background: #EFEFEF;
  }

  &:active {
    background: #DDD;
    box-shadow: none;
    color: #555;
  }
`

const AddButton = styled(Button)`
  margin: 0;
  margin-left: 16px;
`

const GroupSelect = styled(Select)`
  padding: 7px 12px;
  width: 100%;
  margin-bottom: 16px;
  border-radius: 100px;
  background-color: #eaedf5;
  color: #5a5a5a !important;
  font-family: 'DM Sans', sans-serif !important;
  font-size: 16px !important;
  font-weight: 300 !important;
  &.Mui-focused .MuiSelect-select {
    background-color: #eaedf5;
  }
  &:before {
    border: none !important;
  }
  &:after {
    border: none !important;
  }
`

const LoadingMessage = styled.div`
  display: flex;
  align-items: center;
  margin-top: 12px;
  >div {
    flex-shrink: 0;
  }
  p {
    margin-left: 12px;
  }
`

const SendNotification = (props) => {
  const { user } = useContext(AuthContext)
  const theme = useContext(ThemeContext)
  const { openSnackbar } = useContext(DashboardContext)
  const { client } = useChatContext()
  const classes = useStyles();
  const [loading, updateLoading] = useState(false)
  const [groupId, updateGroupId] = useState(0)
  const [title, updateTitle] = useState('')
  const [message, updateMessage] = useState('')
  const [error, updateError] = useState('')
  const [result, updateResult] = useState(null)
  const [usingChat, updateUsingChat] = useState(!!user.messaging)
  const [showModal, setShowModal] = useState(false)
  const [sending, setSending] = useState(false)

  const handleGroupIdChange = (e) => {
    updateGroupId(Number(e.target.value))
  }

  const sendGroupMessage = async (groupId, message) => {
    setSending(true)
    const delay = ms => new Promise(res => setTimeout(res, ms))
    let result =  { success: true, successful : 0, unclaimed: 0, nonnative: 0, failures: [], sent_clients: [], failed_clients: [] }
    try {
      for (let n = 0; n < user.accounts.length; ++n) {
        if (user.accounts[n].groups.includes(groupId)) {
          const clientID = user.accounts[n].user_id
          const portalLinkId = user.accounts[n].linked_portal_id
          const clientLabel = `${user.accounts[n].full_name || user.accounts[n].email_address} (${user.accounts[n].email_address})`
          if (clientID === null) {
            ++result.unclaimed
          } else if (portalLinkId) {
            continue
          } else {
            const getFilter = {
              type: 'portal',
              portal_owner: `${config.STREAMCHAT.portalUserPrefix}${user.owner_id}`,
              members: { $in: [`${config.STREAMCHAT.userPrefix}${clientID}`] },
              covered_user: `${config.STREAMCHAT.userPrefix}${clientID}`,
            }
            const sort = { last_message_at: -1 }
            const options = { watch: false, state: false }

            let channel = null
            const existingChannels = await client.queryChannels(getFilter, sort, options)
            if (existingChannels.length > 0) {
              channel = existingChannels[0]
            } else {
              const res = await getOrCreatePortalChannel(user.id, clientID)
              const channel_id = res.data.result.channel_id
              const newFilter = {
                id: channel_id
              }
              const newChannels = await client.queryChannels(newFilter, sort, options)
              if (newChannels.length > 0) {
                channel = newChannels[0]
              }
            }
            if (channel === null) {
              result.failures.push([clientLabel, 'failed to send'])
              result.failed_clients.push(clientLabel)
            } else {
              const namedMessage = `Hey ${getFirstName(user.accounts[n].full_name)}! ${message}`
              await channel.sendMessage({text: namedMessage})
              await delay(1200)
              ++result.successful
              result.sent_clients.push(clientLabel)
            }
          }
        }
      }
    } finally {
      setSending(false)
    }
    return result
  }

  const handleSend = () => {
    if (loading || sending) {
      return
    }
    if(groupId === 0) {
      updateError('Please select the group that will receive the ' + (usingChat ? 'message.' : 'notification.'))
    } else if(title === '' && !usingChat) {
      updateError('Please enter a title for the notification.')
    } else if(message === '') {
      updateError('Please enter the message.')
    } else if(usingChat) {
      updateLoading(true)
      updateResult(null)
      sendGroupMessage(groupId, message).then((res) => {
        updateLoading(false)
        setShowModal(false)
        updateTitle('')
        updateMessage('')
        updateError('')
        if (!res.success) {
          updateResult(null)
          openSnackbar('There was a problem sending the group message.', true)
        } else if (res.successful === 0 && res.unclaimed === 0 && res.nonnative === 0 && res.failures.length === 0) {
          updateResult(null)
          openSnackbar('There were no accounts in the group.', true)
        } else {
          updateResult(Object.assign({}, res, {usingChat: true}))
          openSnackbar('The group message completed.', false)
          logGroupMessage(user.id, groupId, {
            successful: res.successful,
            unclaimed: res.unclaimed,
            nonnative: res.nonnative,
            sent_clients: res.sent_clients,
            failed_clients: res.failed_clients
          }).catch(() => {})
        }
      }).catch((err) => {
        console.log(err)
        updateLoading(false)
        setShowModal(false)
        updateTitle('')
        updateMessage('')
        updateError('')
        updateResult(null)
        openSnackbar('There was a problem sending the group message.', true)
      })
    } else {
      updateLoading(true)
      sendGroupNotification(user.id, groupId, title, message).then((res) => {
        updateLoading(false)
        setShowModal(false)
        updateTitle('')
        updateMessage('')
        updateError('')
        if (!res.data.success) {
          updateResult(null)
          openSnackbar('There was a problem sending the group notification.', true)
        } else if (res.data.successful === 0 && res.data.unclaimed === 0 && res.data.nonnative === 0 && res.data.failures.length === 0) {
          updateResult(null)
          openSnackbar('There were no accounts in the group.', true)
        } else {
          updateResult(Object.assign({}, res.data, {usingChat: false}))
          openSnackbar('The group notification completed.', false)
        }
      }).catch((err) => {
        console.log(err) 
        updateLoading(false)
        setShowModal(false)
        updateTitle('')
        updateMessage('')
        updateError('')
        updateResult(null)
        openSnackbar('There was a problem sending the group notification.', true)
      })
    }
  }

  let chatToggleStyle = { cursor: 'pointer', padding: '0 4px' }
  let notifToggleStyle = { cursor: 'pointer', padding: '0 4px' }
  if (usingChat) {
    chatToggleStyle.color = theme.color.primary
    chatToggleStyle.textDecoration = 'underline'
    chatToggleStyle.textUnderlineOffset = '5px'
  } else {
    notifToggleStyle.color = theme.color.primary
    notifToggleStyle.textDecoration = 'underline'
    notifToggleStyle.textUnderlineOffset = '5px'
  }

  return (
    <React.Fragment>
      <Container className={props.className}>
        <HeaderContainer>
          {!!user.messaging ? (
            <Label>
              <PersonAdd /> Send a Group <div><span style={chatToggleStyle} onClick={() => updateUsingChat(true)}>Message</span> | <span style={notifToggleStyle} onClick={() => updateUsingChat(false)}>Notification</span></div>
            </Label>
          ) : (
            <Label>
              <PersonAdd /> Send a Group Notification
            </Label>
          )}
        </HeaderContainer>
        {user.groups.length === 0 ? (
          <p>Before you can send a group {usingChat ? 'message' : 'notification'}, you must create a group.</p>
        ) : (
          <>
            <div>
              <InputLabel id="group-select-label" style={{color: '#676767', fontFamily: '"DM Sans", sans-serif', fontSize: '14px', fontWeight: '700', marginBottom: '8px'}}>Group</InputLabel>
              <GroupSelect value={groupId} labelId="group-select-label" label="Group" disableUnderline onChange={handleGroupIdChange}>
                <MenuItem style={{color: '#5a5a5a', fontFamily: '"DM Sans", sans-serif', fontSize: '16px', fontWeight: '300'}} value={0}>Select...</MenuItem>
                {user.groups.map((entry, index) => (
                  <MenuItem style={{color: '#5a5a5a', fontFamily: '"DM Sans", sans-serif', fontSize: '16px', fontWeight: '300'}} key={index} value={entry.id}>{entry.name}</MenuItem>
                ))}
              </GroupSelect>
            </div>
            {!usingChat && (
              <Input
                label="Title"
                value={title}
                onChange={updateTitle}
              />
            )}
            <Input
              label="Message"
              value={message}
              onChange={updateMessage}
            />
            {error !== '' && 
              <ErrorMessage>
                {error}
              </ErrorMessage>
            }
            <ButtonContainer>
              <ClearButton
                label="Clear"
                onClick={() => { updateGroupId(0); updateTitle(''); updateMessage(''); updateError(''); updateResult(null); }}
              />
              <AddButton 
                label='Preview'
                disabled={loading || sending}
                onClick={() => setShowModal(true)}
              />
            </ButtonContainer>
            {sending &&
              <LoadingMessage>
                <CircularProgress className={classes.buttonProgress} size={24}/> <p>Sending group messages. This will take a moment depending on the number of clients.</p>
              </LoadingMessage>
            }
            {result !== null && (
              <div style={{marginTop: '10px'}}>
                {result.successful !== 0 && (
                  <p>Number of successful {result.usingChat ? 'messages' : 'notifications'}: {result.successful}</p>
                )}
                {result.unclaimed !== 0 && (
                  <p>Number of unclaimed accounts: {result.unclaimed}</p>
                )}
                {result.nonnative !== 0 && (
                  <p>Number of accounts not using app: {result.nonnative}</p>
                )}
                {result.failures.map((entry, index) => (
                  <p key={index}>{entry[0]}: {entry[1]}</p>
                ))}
              </div>
            )}
          </>
        )}
      </Container>
      <Modal
        title={`Confirm ${usingChat? 'Message' : 'Notification'}`}
        message={`Hey {client's name}! ${message}`}
        submitLabel={`Send ${usingChat? 'Message' : 'Notification'}`}
        open={showModal}
        onClose={() => setShowModal(false)}
        handleSubmit={handleSend}
        disabled={loading || sending}
        isLoading={loading || sending}
      >
        {usingChat &&<p style={{fontSize: '.9rem', color: '#919191'}}>*Coach accounts will not receive group messages since they do not have a shared trainer channel.</p>}
      </Modal>
    </React.Fragment>
  )
}

export default SendNotification
